import { Box, Button, Flex, Stack } from '@chakra-ui/react'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import { SHWError } from '@/common/error'
import chakraTokenToVar from '@/utils/chakraTokenToVar'
import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import { useIsMounted } from '@/client/hooks'
import useForceUpdate from '@/client/hooks/useForceUpdate'
import { trackEvent } from '@/client/services/analytics'
import isSerializedRichTextProbably from '@/client/services/richText/isSerializedRichTextProbably'
import useLockUpdate from '@/client/services/update/useLockUpdate'

import TextWithLines from '@/components/TextWithLines/TextWithLines'
import { useToaster } from '@/components/Toast/useToaster'
import RichText from '@/components/UI/RichTextEditor/RichText'

import FormBlocksHorizontal from './FormBlocksHorizontal'
import FormBlocksVertical from './FormBlocksVertical'
import FormInput from './FormInput/FormInput'
import {
  basicValidate,
  blocksToRows,
  filterDependentFormInputValues,
  isInput,
  resolveFormDependencies,
} from './helpers'
import {
  type AlignInputs,
  type FormBlock,
  type FormBlocksComponentProps,
  FormContext,
  type FormDirection,
  type FormErrors,
  type FormOnChange,
  type FormOnSubmit,
  type FormOnValidate,
  type FormQuestion,
  type FormValidationResult,
  type FormValue,
  type FormValues,
} from './types'

export interface FormState {
  submitting: boolean
  error: string | JSX.Element | null
  errors: FormErrors
}

export interface FormRef {
  submit: () => Promise<boolean>
  validate: () => Promise<FormValidationResult>
  setFormState: React.Dispatch<React.SetStateAction<FormState>>
  values: FormValues
  validationFailed: boolean
}

export interface FormProps {
  name: string
  blocks: FormBlock[]
  disabled?: boolean
  direction?: FormDirection
  onValidate?: FormOnValidate
  onChange?: FormOnChange
  // Since FormInputs by default handles logging/toasting in case of error
  // executing onSubmit, there is no need to try/catch your onSubmit fn
  // unless you want very specific error-handling behavior. Ideally, you
  // will never need it.
  onSubmit: FormOnSubmit
  hideErrorMessage?: boolean
  // `submitting` can be used to set `isLoading` on the Submit button and `isDisabled` on other buttons
  FooterComponent?:
    | React.FunctionComponent<{ disabled: boolean; submitting: boolean; submitButtonText?: string }>
    | undefined
  BlocksComponent?: React.FunctionComponent<FormBlocksComponentProps> | null
  alignInputs?: AlignInputs
  submitButtonText?: string
}

export const FormDefaultFooter = ({
  disabled,
  submitting,
  submitButtonText,
}: {
  disabled: boolean
  submitting: boolean
  submitButtonText?: string
}) => (
  <Flex direction="row" mt={9} justifyContent="flex-end">
    <Button isDisabled={disabled} isLoading={submitting} type="submit">
      {submitButtonText ?? 'Submit'}
    </Button>
  </Flex>
)

const Form = React.forwardRef<FormRef, FormProps>(
  (
    {
      name,
      blocks,
      disabled = false,
      direction = 'vertical',
      hideErrorMessage = false,
      onValidate,
      onSubmit,
      onChange,
      FooterComponent = FormDefaultFooter,
      BlocksComponent = null,
      alignInputs,
      submitButtonText,
    }: FormProps,
    ref
  ): JSX.Element => {
    const user = useUser()
    const toast = useToaster()
    const forceUpdate = useForceUpdate()
    const scrollAnchorRef = React.useRef<HTMLDivElement | null>(null)
    const isMounted = useIsMounted()
    const [{ submitting, error, errors }, setFormState] = React.useState<FormState>({
      submitting: false,
      error: null,
      errors: {},
    })
    const formValidationFailed = error != null || Object.keys(errors).length > 0

    const formValuesDefault = React.useMemo(() => {
      const formValues: FormValues = {}
      blocks.forEach(block => {
        if (block.display === 'display') {
          return
        }
        // Do not overwrite
        if (formValues[block.name] != null) {
          return
        }
        if (block.type === 'multi-checkbox' || block.type === 'multi-select') {
          formValues[block.name] = block.initialValue ?? []
        } else if (block.type === 'date' || block.type === 'select-tab') {
          formValues[block.name] = block.initialValue ?? null
        } else {
          formValues[block.name] = block.initialValue ?? ''
        }
      })
      return formValues
    }, [blocks])
    const formValues = React.useRef<FormValues>(formValuesDefault)
    const inputDependencies = React.useMemo(() => {
      const deps = new Set<string>()

      blocks.forEach(block => {
        if (block.dependencies != null) {
          block.dependencies.keys.forEach(dep => {
            deps.add(dep)
          })
        }
      })

      return deps
    }, [blocks])

    // Wrapping with resolveInputRowDependencies() outside of useMemo()
    // because it needs access to "current" form values
    const formRows = resolveFormDependencies(
      React.useMemo(() => blocksToRows(blocks), [blocks]),
      formValues.current
    )

    const validate = React.useCallback(async (): Promise<FormValidationResult> => {
      if (submitting) {
        return { success: false, message: 'Submitting', errors: null }
      }
      setFormState({ submitting: true, error: null, errors: {} })

      let validationErrors: FormErrors | null = null
      let validationErrorMessage: string | null =
        'Please address the following issues and submit again.'

      const values = filterDependentFormInputValues({ blocks, values: { ...formValues.current } })

      try {
        validationErrors = basicValidate(values, blocks)
        if (onValidate != null) {
          const validateResult = await onValidate(values, validationErrors)
          if (validateResult != null) {
            validationErrorMessage = validateResult.message
            validationErrors = validateResult.errors
          }
        }
      } catch (err) {
        setFormState({ submitting: false, error: err?.message ?? err, errors: {} })
        scrollAnchorRef.current?.scrollIntoView({ block: 'center' })
        trackEvent({
          category: 'Form - Local validation failed',
          action: name,
        })
        return { success: false, message: 'Validation failed', errors: null }
      }
      if (validationErrors != null && Object.keys(validationErrors).length > 0) {
        setFormState({
          submitting: false,
          error: validationErrorMessage,
          errors: validationErrors,
        })
        scrollAnchorRef.current?.scrollIntoView({ block: 'center' })
        trackEvent({
          category: 'Form - Local input validation failed',
          action: name,
        })
        return {
          success: false,
          message: validationErrorMessage ?? 'Validation failed',
          errors: validationErrors,
        }
      }
      setFormState({ submitting: true, error: null, errors: {} })
      return { success: true, message: 'Validation succeeded', errors: null }
    }, [blocks, name, onValidate, submitting])

    const handleSubmit = React.useCallback(
      async (e?: React.SyntheticEvent<HTMLFormElement>): Promise<boolean> => {
        e?.preventDefault()
        const validationResult = await validate()
        if (validationResult.success === false) {
          return false
        }

        const values = filterDependentFormInputValues({
          blocks,
          values: { ...formValues.current },
        })
        try {
          setFormState({ submitting: true, error: null, errors: {} })

          await onSubmit(values)
          trackEvent({
            category: 'Form - Submission successful',
            action: name,
          })
          if (isMounted()) {
            setFormState({ submitting: false, error: null, errors: {} })
          }
          return true
        } catch (err) {
          logger.warning('Form - Submission failed', { userId: user.id, name, values, err })
          trackEvent({
            category: 'Form - Submission failed',
            action: name,
          })

          if (err instanceof SHWError && isSerializedRichTextProbably(err.externalMessage)) {
            toast({
              title: <RichText content={err.externalMessage} />,
              status: 'error',
            })
            if (isMounted()) {
              setFormState({
                submitting: false,
                error: <RichText content={err.externalMessage} />,
                errors: {},
              })
            }
          } else {
            toast({
              title: (
                <TextWithLines
                  text={`${err?.message ?? err}`}
                  maxLines={Number.POSITIVE_INFINITY}
                />
              ),
              status: 'error',
            })
            if (isMounted()) {
              setFormState({
                submitting: false,
                error: err?.message ?? err,
                errors: {},
              })
            }
          }

          if (isMounted()) {
            scrollAnchorRef.current?.scrollIntoView({ block: 'center' })
          }
        }
        return false
      },
      [validate, blocks, onSubmit, name, isMounted, user.id, toast]
    )

    const formContext = React.useMemo(
      () => ({
        disabled,
        submitting,
        direction,
        errors,
        onChange(inputName: string, value: FormValue) {
          formValues.current[inputName] = value
          if (inputDependencies.has(inputName)) {
            forceUpdate()
          }
          if (onChange != null) {
            onChange(inputName, value, { ...formValues.current })
          }
        },
        onSubmit: handleSubmit,
      }),
      [
        disabled,
        submitting,
        direction,
        errors,
        handleSubmit,
        inputDependencies,
        onChange,
        forceUpdate,
      ]
    )

    React.useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit,
        values: formValues.current,
        validate,
        setFormState,
        validationFailed: formValidationFailed,
      }),
      [handleSubmit, validate, formValidationFailed]
    )

    const DefaultBlocksComponent =
      direction === 'horizontal' ? FormBlocksHorizontal : FormBlocksVertical

    const renderInput = (block: FormQuestion) => {
      if (!isInput(block)) {
        throw new Error('Cannot display form question')
      }
      return <FormInput key={block.name} input={block} />
    }

    useLockUpdate()

    return (
      <FormContext.Provider value={formContext}>
        <form action="#" method="post" style={{ flex: 1 }} onSubmit={handleSubmit}>
          <Flex direction="column">
            <Box minWidth="1px" minHeight="1px" ref={scrollAnchorRef} />
            {error == null || hideErrorMessage ? null : (
              <Stack direction="row" spacing={2} color="fg.error" mb={5} alignItems="center">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  color={chakraTokenToVar('fg.error')}
                  size="lg"
                />
                <Box>{error}</Box>
              </Stack>
            )}
            {BlocksComponent == null ? (
              <DefaultBlocksComponent
                rows={formRows}
                errors={errors}
                renderQuestion={renderInput}
                alignInputs={alignInputs}
              />
            ) : (
              <BlocksComponent
                rows={formRows}
                errors={errors}
                renderQuestion={renderInput}
                alignInputs={alignInputs}
              />
            )}
          </Flex>
          <FooterComponent
            disabled={disabled}
            submitting={submitting}
            submitButtonText={submitButtonText}
          />
        </form>
      </FormContext.Provider>
    )
  }
)

export default Form
